
import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { createAuthorizationPayment } from '../api/authPaymentApi';
import { Loader2 } from 'lucide-react';
import { toast } from '@/components/ui/sonner';

interface CreateAuthPaymentDialogProps {
  subscriptionId: string;
  courierId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export function CreateAuthPaymentDialog({
  subscriptionId,
  courierId,
  open,
  onOpenChange,
  onSuccess
}: CreateAuthPaymentDialogProps) {
  const [amount, setAmount] = useState<number>(1);
  const [receiptUrl, setReceiptUrl] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);

  const handleCreatePayment = async () => {
    setLoading(true);
    try {
      const { success, payment, error } = await createAuthorizationPayment(
        subscriptionId,
        courierId,
        amount,
        receiptUrl
      );

      if (success && payment) {
        onSuccess();
        onOpenChange(false);
        toast.success('Авторизационный платеж создан успешно');
      } else {
        toast.error(`Ошибка создания платежа: ${error}`);
      }
    } catch (err: any) {
      toast.error(`Ошибка: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Создание авторизационного платежа</DialogTitle>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="amount" className="text-right">
              Сумма
            </Label>
            <Input
              id="amount"
              type="number"
              value={amount}
              onChange={(e) => setAmount(Number(e.target.value))}
              className="col-span-3"
              min="1"
              step="1"
            />
          </div>
          
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="receiptUrl" className="text-right">
              Ссылка для оплаты
            </Label>
            <Input
              id="receiptUrl"
              type="text"
              value={receiptUrl}
              onChange={(e) => setReceiptUrl(e.target.value)}
              placeholder="https://payment.example.com/auth/123"
              className="col-span-3"
            />
          </div>
        </div>

        <DialogFooter>
          <Button 
            variant="outline" 
            onClick={() => onOpenChange(false)}
          >
            Отмена
          </Button>
          <Button 
            onClick={handleCreatePayment}
            disabled={loading || !receiptUrl}
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Создание...
              </>
            ) : (
              'Создать платеж'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
